import { Box } from '@mui/material';
import React from 'react';

// ----------------------- Contexts -----------------------
const FactorsContext = React.createContext({ rowFactor: 1, colFactor: 1 });
const CollectorContext = React.createContext({ reportLeaf: () => {} });
const RegionContext   = React.createContext({ rowIndex: 1, rowSpan: 1, colIndex: 1, colSpan: 1 });

// ----------------------- Math helpers -----------------------
const gcd = (a, b) => {
  while (b !== 0) {
    [a, b] = [b, a % b];
  }
  return Math.abs(a);
};

const lcm2 = (a, b) => {
  if (a === 0 || b === 0) return 0;
  return Math.abs((a * b) / gcd(a, b));
};

const lcmAll = numbers => {
  if (!Array.isArray(numbers) || numbers.length === 0) return 1;
  return numbers.reduce((acc, num) => lcm2(acc, num), 1);
};

// ----------------------- Core components -----------------------
export const SpacerLayout = ({ children }) => {
  // Grid tracks; start at 1×1 so we can mount children immediately (effects fire once)
  const [rows, setRows] = React.useState(1);
  const [cols, setCols] = React.useState(1);

  // Phase 1 collection bucket (cleared every render)
  const leafPairsRef = React.useRef([]);
  leafPairsRef.current = [];

  const reportLeaf = (rowFactor, colFactor) => {
    leafPairsRef.current.push({ rowFactor, colFactor });
  };

  // After each render, compute totals and update if changed (causes the second render)
  React.useLayoutEffect(() => {
    const pairs = leafPairsRef.current;
    if (pairs.length === 0) return;

    const nextRows = lcmAll(pairs.map(p => p.rowFactor)) || 1;
    const nextCols = lcmAll(pairs.map(p => p.colFactor)) || 1;

    if (nextRows !== rows) setRows(nextRows);
    if (nextCols !== cols) setCols(nextCols);
  });

  const rootRegion = { rowIndex: 1, rowSpan: rows, colIndex: 1, colSpan: cols };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'grid',
        gap: 0,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        minWidth: 0,
        minHeight: 0,
        m: -1
      }}
    >
      {/* Single tree: children mount once. First render collects; second render lays out. */}
      <CollectorContext.Provider value={{ reportLeaf }}>
        <RegionContext.Provider value={rootRegion}>
          <FactorsContext.Provider value={{ rowFactor: 1, colFactor: 1 }}>
            {children}
          </FactorsContext.Provider>
        </RegionContext.Provider>
      </CollectorContext.Provider>
    </Box>
  );
};

// Splits its own region by weights and forwards factors & regions to descendants
export const Spacer = ({ vertical = false, weights, children }) => {
  const parentRegion  = React.useContext(RegionContext);
  const parentFactors = React.useContext(FactorsContext);

  const childElements = React.Children.toArray(children);

  // Fill weights to match children count (default 1)
  const rawWeights = Array.isArray(weights) ? weights : [];
  const filledWeights = rawWeights.slice(0, childElements.length);
  while (filledWeights.length < childElements.length) filledWeights.push(1);

  const totalWeight = filledWeights.reduce((sum, value) => sum + value, 0) || 1;

  // Phase 1: update denominators (factors) for descendants
  const nextFactors = vertical
    ? { rowFactor: parentFactors.rowFactor * totalWeight, colFactor: parentFactors.colFactor }
    : { rowFactor: parentFactors.rowFactor,             colFactor: parentFactors.colFactor * totalWeight };

  // Phase 2: compute per-child regions and wrap each subtree with Region provider
  let offset = 0;
  const nextChildren = childElements.map((child, index) => {
    const weight = filledWeights[index];

    const childRegion = vertical
      ? {
          rowIndex: parentRegion.rowIndex + (parentRegion.rowSpan * offset) / totalWeight,
          rowSpan:  (parentRegion.rowSpan  * weight) / totalWeight,
          colIndex: parentRegion.colIndex,
          colSpan:  parentRegion.colSpan
        }
      : {
          rowIndex: parentRegion.rowIndex,
          rowSpan:  parentRegion.rowSpan,
          colIndex: parentRegion.colIndex + (parentRegion.colSpan * offset) / totalWeight,
          colSpan:  (parentRegion.colSpan  * weight) / totalWeight
        };

    offset += weight;

    return (
      <RegionContext.Provider key={index} value={childRegion}>
        {child}
      </RegionContext.Provider>
    );
  });

  return (
    <FactorsContext.Provider value={nextFactors}>
      <Box sx={{ display: 'contents' }}>
        {nextChildren}
      </Box>
    </FactorsContext.Provider>
  );
};

// Leaf: reports factors during render and uses region during layout
export const SpacerLayoutItem = ({ children }) => {
  // Phase 1: report factors (this does not trigger re-mounts)
  const { reportLeaf } = React.useContext(CollectorContext);
  const { rowFactor, colFactor } = React.useContext(FactorsContext);
  reportLeaf(rowFactor, colFactor);

  // Phase 2: read region (props override context if provided)
  const region = React.useContext(RegionContext);
  const rowIndex = region.rowIndex;
  const rowSpan = region.rowSpan;
  const colIndex = region.colIndex;
  const colSpan = region.colSpan;

  return (
    <Box
      sx={{
        gridRow: `${rowIndex} / span ${rowSpan}`,
        gridColumn: `${colIndex} / span ${colSpan}`,
        minWidth: 0,
        minHeight: 0,
        p: 1
      }}
    >
      {children}
    </Box>
  );
};
